// Som e aviso na tela ao chegar mensagem no Chat Interno, configurados por
// usuário no Perfil (ui_settings), no mesmo modelo dos alertas das conversas:
//   internal_chat_audio_alert   'none' | 'all' | 'mentions'
//   internal_chat_notice_alert  'none' | 'all' | 'mentions'
//   internal_chat_alert_tone    tom próprio, diferente do das conversas
//   internal_chat_always_alert  avisar também com o Fluxi aberto na tela
// 'mentions' = menções a mim e mensagens diretas (DM).
// O aviso é um cartão dentro do próprio Fluxi (AvisosDoChatInterno.vue), não
// uma notificação do sistema operacional.
import { reactive } from 'vue';
import WindowVisibilityHelper from './WindowVisibilityHelper';

export const TOM_PADRAO_DO_CHAT = 'chime';
const CAMINHO_DOS_TONS = '/audio/dashboard/';
const INTERVALO_MINIMO_DO_SOM = 3000;
const DURACAO_DO_AVISO = 10000;
const MAXIMO_DE_AVISOS = 4;

export const configDoChatInterno = (uiSettings = {}) => ({
  som: uiSettings.internal_chat_audio_alert || 'none',
  aviso: uiSettings.internal_chat_notice_alert || 'none',
  tom: uiSettings.internal_chat_alert_tone || TOM_PADRAO_DO_CHAT,
  sempre: uiSettings.internal_chat_always_alert !== false,
});

const combina = (preferencia, relevante) =>
  preferencia === 'all' || (preferencia === 'mentions' && relevante);

/**
 * Decide se a mensagem avisa, e como. Função pura: tudo o que ela precisa
 * vem por parâmetro, para ser testada sem navegador.
 */
export const decidirAlerta = ({
  mensagem,
  canal,
  usuarioId,
  canalAbertoId,
  janelaVisivel,
  config,
}) => {
  const nada = { som: false, aviso: false };
  if (!mensagem || mensagem.sender?.id === usuarioId) return nada;

  // Quem está com o canal aberto na tela já está vendo a mensagem.
  if (janelaVisivel && canalAbertoId === mensagem.internal_chat_channel_id) {
    return nada;
  }
  if (janelaVisivel && !config.sempre) return nada;

  const mencoes = mensagem.content_attributes?.mentioned_user_ids || [];
  const fuiMencionado = mencoes.includes(usuarioId);
  // Canal silenciado só avisa quando a pessoa é mencionada.
  if (canal?.muted && !fuiMencionado) return nada;

  const relevante = fuiMencionado || Boolean(canal?.is_dm);
  return {
    som: combina(config.som, relevante),
    aviso: combina(config.aviso, relevante),
  };
};

// Canal do Chat Interno aberto na tela agora, pela URL
// (/app/accounts/:id/internal-chat/channels/:channelId[/thread/...]).
export const canalAbertoNaUrl = (caminho = '') => {
  const achado = caminho.match(/\/internal-chat\/channels\/(\d+)/);
  return achado ? Number(achado[1]) : null;
};

export const tituloDoAviso = ({ mensagem, canal }) => {
  const remetente = mensagem.sender?.name || 'Chat Interno';
  if (canal?.is_dm || !canal?.name) return remetente;
  return `${remetente} em #${canal.name}`;
};

export const textoDoAviso = mensagem => {
  const texto = (mensagem.content || '').trim();
  if (!texto) return '';
  return texto.length > 140 ? `${texto.slice(0, 137)}...` : texto;
};

// Avisos na tela, lidos pelo componente AvisosDoChatInterno.vue.
export const avisos = reactive([]);

export function fecharAviso(id) {
  const indice = avisos.findIndex(aviso => aviso.id === id);
  if (indice === -1) return;
  clearTimeout(avisos[indice].timer);
  avisos.splice(indice, 1);
}

const agendarSaida = aviso => {
  aviso.timer = setTimeout(() => fecharAviso(aviso.id), DURACAO_DO_AVISO);
};

// Aviso que chegou com o Fluxi em segundo plano espera a pessoa voltar para
// começar a contar os 10 segundos: senão sumiria antes de ser visto.
if (typeof window !== 'undefined') {
  window.addEventListener('focus', () => {
    avisos.filter(aviso => !aviso.timer).forEach(agendarSaida);
  });
}

export const mostrarAviso = ({ mensagem, canal, janelaVisivel }) => {
  const canalId = mensagem.internal_chat_channel_id;
  // Um aviso por canal: a mensagem nova substitui a anterior.
  avisos
    .filter(aviso => aviso.canalId === canalId)
    .forEach(aviso => fecharAviso(aviso.id));
  const aviso = {
    id: `${canalId}-${mensagem.id}`,
    canalId,
    titulo: tituloDoAviso({ mensagem, canal }),
    texto: textoDoAviso(mensagem),
    avatar: mensagem.sender?.thumbnail || '',
    nome: mensagem.sender?.name || '',
    timer: null,
  };
  avisos.unshift(aviso);
  while (avisos.length > MAXIMO_DE_AVISOS) {
    fecharAviso(avisos[avisos.length - 1].id);
  }
  if (janelaVisivel) agendarSaida(aviso);
};

class AlertasDoChatInterno {
  constructor() {
    this.audio = null;
    this.tomCarregado = null;
    this.ultimoSom = 0;
  }

  tocar(tom) {
    const agora = Date.now();
    if (agora - this.ultimoSom < INTERVALO_MINIMO_DO_SOM) return;
    this.ultimoSom = agora;
    if (this.tomCarregado !== tom) {
      this.audio = new Audio(`${CAMINHO_DOS_TONS}${tom}.mp3`);
      this.tomCarregado = tom;
    }
    // O navegador bloqueia som antes da primeira interação: não há o que fazer.
    this.audio.play().catch(() => {});
  }

  aoReceber({ mensagem, canal, usuarioId, uiSettings }) {
    const config = configDoChatInterno(uiSettings);
    const canalAbertoId = canalAbertoNaUrl(window.location.pathname);
    const janelaVisivel = WindowVisibilityHelper.isWindowVisible();
    const { som, aviso } = decidirAlerta({
      mensagem,
      canal,
      usuarioId,
      canalAbertoId,
      janelaVisivel,
      config,
    });
    if (som) this.tocar(config.tom);
    if (aviso) mostrarAviso({ mensagem, canal, janelaVisivel });
  }
}

export default new AlertasDoChatInterno();
